import type {
  IntegrationApplication,
  WidgetId,
} from '@wix/members-area-app-definitions';
import { InstallLocation } from '@wix/members-area-app-definitions';
import type { ComponentRef, FlowEditorSDK } from '@wix/yoshi-flow-editor';
import { debounce } from 'lodash';

import {
  SETTINGS_SLOT_ID_TO_MULTI_STATE_BOX_STATE,
  SETTINGS_SLOT_ROLE_ID_TO_MULTI_STATE_BOX_STATE,
  SLOT_ID_TO_MULTI_STATE_BOX_STATE,
  SLOT_ROLE_ID_TO_MULTI_STATE_BOX_STATE,
} from '../../../constants';
import {
  refreshApp,
  runAndWaitForApproval,
} from '../../../editor/editor-sdk-wrappers/document';
import { globalAppState } from '../../../editor/services';
import { log, toMonitored } from '../../../editor/services/monitor';
import {
  getProfilePageBobWidgetRef,
  getSettingsPageWidgetRef,
} from '../../../editor/services/page-ref';
import { getWidgetSlots } from '../../../editor/services/slots';
import type {
  AddedWidgetConfig,
  WidgetPluginPointer,
  WidgetSlot,
} from '../../../types';
import { getIsEditor3, getSlotDefinition } from '../../../utils';
import { installProfilePageIfMissing } from '../installation';
import { navigateToSection } from './navigation';
import { updateProfilePageRoutes, updateSettingsPageRoutes } from './routes';

type DeboncedNavigateToAddedWidgetProps = {
  editorSDK: FlowEditorSDK;
  settingsAddedWidgetsConfigs: AddedWidgetConfig[];
  profileAddedWidgetsConfigs: AddedWidgetConfig[];
};

const groupDefinitionsByLocation = (definitions: IntegrationApplication[]) => {
  const definitionsMap = {
    settingsPage: [] as IntegrationApplication[],
    profilePage: [] as IntegrationApplication[],
  };

  definitions.forEach((definition) => {
    if (definition.installLocation === InstallLocation.Settings) {
      return definitionsMap.settingsPage.push(definition);
    }

    definitionsMap.profilePage.push(definition);
  });

  return definitionsMap;
};

const isPluginAlreadyAdded = (
  widgetsSlots: WidgetSlot[],
  widgetPluginPointer: WidgetPluginPointer,
) => {
  return widgetsSlots.some(({ pluginInfo }) => {
    return (
      pluginInfo?.widgetId === widgetPluginPointer.widgetId &&
      pluginInfo.appDefinitionId === widgetPluginPointer.appDefinitionId
    );
  });
};

const getNotInstalledDefinitions = (
  definitions: IntegrationApplication[],
  widgetSlots: WidgetSlot[],
) => {
  return definitions.reduce((notInstalledDefinitions, definition) => {
    return isPluginAlreadyAdded(widgetSlots, definition)
      ? notInstalledDefinitions
      : [...notInstalledDefinitions, definition];
  }, [] as IntegrationApplication[]);
};

const addWidgetPluginToProfilePage = async (
  editorSDK: FlowEditorSDK,
  definition: IntegrationApplication,
  hostCompRef: ComponentRef,
  slot: WidgetSlot,
) => {
  const {
    appDefinitionId,
    widgetId,
    visibleForRoles,
    socialHome,
    social,
    urlOverride,
  } = definition;
  const slotDefinition = getSlotDefinition(slot, hostCompRef);

  await toMonitored(
    'ma-split.install.add-widget-plugin-profile',
    () => {
      return editorSDK.tpa.widgetPlugins.addWidgetPlugin('', {
        widgetPluginPointer: {
          appDefinitionId,
          widgetId,
        },
        ...slotDefinition,
      });
    },
    {
      widgetPluginPointerAppDefinitionId: appDefinitionId,
      widgetPluginPointerWidgetId: widgetId,
      hostCompRef,
      slotId: slot.id,
      slotRole: slot.role,
      slotCompRefId: slot.compRef?.id,
    },
  );

  return {
    widgetId,
    vfr: visibleForRoles,
    home: socialHome,
    private: !social,
    path: urlOverride!,
    state: getIsEditor3()
      ? SLOT_ID_TO_MULTI_STATE_BOX_STATE[slot.id]
      : SLOT_ROLE_ID_TO_MULTI_STATE_BOX_STATE[slot.role],
  };
};

export const addWidgetPluginToSettingsPage = async (
  editorSDK: FlowEditorSDK,
  definition: IntegrationApplication,
  hostCompRef: ComponentRef,
  slot: WidgetSlot,
) => {
  const { appDefinitionId, widgetId, visibleForRoles } = definition;
  const slotDefinition = getSlotDefinition(slot, hostCompRef);

  await toMonitored(
    'ma-split.install.add-widget-plugin-settings',
    () => {
      return editorSDK.tpa.widgetPlugins.addWidgetPlugin('', {
        widgetPluginPointer: {
          appDefinitionId,
          widgetId,
        },
        ...slotDefinition,
      });
    },
    {
      widgetPluginPointerAppDefinitionId: appDefinitionId,
      widgetPluginPointerWidgetId: widgetId,
      hostCompRef,
      slotId: slot.id,
      slotRole: slot.role,
      slotCompRefId: slot.compRef?.id,
    },
  );

  return {
    widgetId,
    vfr: visibleForRoles,
    path: definition.urlOverride!,
    state: getIsEditor3()
      ? SETTINGS_SLOT_ID_TO_MULTI_STATE_BOX_STATE[slot.id]
      : SETTINGS_SLOT_ROLE_ID_TO_MULTI_STATE_BOX_STATE[slot.role],
  };
};

const addWidgetPluginsToSettingsPage = async (
  editorSDK: FlowEditorSDK,
  definitions: IntegrationApplication[],
) => {
  const widgetRef = await getSettingsPageWidgetRef(editorSDK);
  const widgetSlots = await getWidgetSlots(editorSDK, widgetRef);
  // TODO: See if routes experiment pays of and use it.
  const emptySlots = widgetSlots.filter(({ pluginInfo }) => !pluginInfo);
  const addedWidgetsConfigs: AddedWidgetConfig[] = [];

  const addWidgetsAction = async () => {
    const addWidgetPromises = [];
    const notInstalledDefinitions = getNotInstalledDefinitions(
      definitions,
      widgetSlots,
    );

    for (const definition of notInstalledDefinitions) {
      const slot = emptySlots.shift();

      if (!slot) {
        log(
          `Settings Page: all available slots are taken, widget ${definition.widgetId} failed to install`,
        );
        break;
      }

      const addWidgetPromise = (async () => {
        const config = await addWidgetPluginToSettingsPage(
          editorSDK,
          definition,
          widgetRef,
          slot,
        );
        addedWidgetsConfigs.push(config);
      })();

      addWidgetPromises.push(addWidgetPromise);
    }

    return Promise.allSettled(addWidgetPromises);
  };

  await runAndWaitForApproval(editorSDK, addWidgetsAction);
  await updateSettingsPageRoutes(editorSDK, addedWidgetsConfigs);

  return addedWidgetsConfigs;
};

const addWidgetPluginsToProfilePage = async (
  editorSDK: FlowEditorSDK,
  definitions: IntegrationApplication[],
) => {
  const widgetRef = await getProfilePageBobWidgetRef(editorSDK);
  const widgetSlots = await getWidgetSlots(editorSDK, widgetRef);
  // TODO: See if routes experiment pays of and use it.
  const emptySlots = widgetSlots.filter(({ pluginInfo }) => !pluginInfo);
  const addedWidgetsConfigs: AddedWidgetConfig[] = [];

  const addWidgetsAction = async () => {
    const addWidgetPromises = [];
    const notInstalledDefinitions = getNotInstalledDefinitions(
      definitions,
      widgetSlots,
    );

    for (const definition of notInstalledDefinitions) {
      const slot = emptySlots.shift();

      if (!slot) {
        log(
          `Profile Page: all available slots are taken, widget ${definition.widgetId} failed to install`,
        );
        break;
      }

      const addWidgetPromise = (async () => {
        const config = await addWidgetPluginToProfilePage(
          editorSDK,
          definition,
          widgetRef,
          slot,
        );
        addedWidgetsConfigs.push(config);
      })();

      addWidgetPromises.push(addWidgetPromise);
    }

    return Promise.allSettled(addWidgetPromises);
  };

  await runAndWaitForApproval(editorSDK, addWidgetsAction);
  await updateProfilePageRoutes(editorSDK, addedWidgetsConfigs);

  return addedWidgetsConfigs;
};

const debouncedMaybeNavigateToAddedWidget = debounce(
  async ({
    editorSDK,
    settingsAddedWidgetsConfigs,
    profileAddedWidgetsConfigs,
  }: DeboncedNavigateToAddedWidgetProps) => {
    const isSingleSettingWidget =
      settingsAddedWidgetsConfigs.length === 1 &&
      !profileAddedWidgetsConfigs.length;
    const isSingleProfileWidget =
      profileAddedWidgetsConfigs.length === 1 &&
      !settingsAddedWidgetsConfigs.length;

    if (isSingleSettingWidget || isSingleProfileWidget) {
      const widgetId = isSingleSettingWidget
        ? settingsAddedWidgetsConfigs[0].widgetId
        : profileAddedWidgetsConfigs[0].widgetId;

      return navigateToSection(editorSDK, widgetId);
    }
  },
  1000,
);

export const addWidgetsPlugins = async (
  editorSDK: FlowEditorSDK,
  definitions: IntegrationApplication[],
  shouldNavigate = false,
) => {
  const flowAPI = globalAppState.getFlowAPI()!;
  const definitionsByLocation = groupDefinitionsByLocation(definitions);

  const settingsPagePluginsAddPromise =
    definitionsByLocation.settingsPage.length > 0
      ? addWidgetPluginsToSettingsPage(
          editorSDK,
          definitionsByLocation.settingsPage,
        )
      : Promise.resolve([]);

  const profilePagePluginsAddPromise =
    definitionsByLocation.profilePage.length > 0
      ? installProfilePageIfMissing({ editorSDK, flowAPI }).then(() => {
          return addWidgetPluginsToProfilePage(
            editorSDK,
            definitionsByLocation.profilePage,
          );
        })
      : Promise.resolve([]);

  const [settingsAddedWidgetsConfigs, profileAddedWidgetsConfigs] =
    await Promise.all([
      settingsPagePluginsAddPromise,
      profilePagePluginsAddPromise,
    ]);
  await refreshApp(editorSDK);

  if (shouldNavigate) {
    // Need to debounce the navigation because the app properties are not updated yet (refreshApp uses debounce inside)
    return debouncedMaybeNavigateToAddedWidget({
      editorSDK,
      settingsAddedWidgetsConfigs,
      profileAddedWidgetsConfigs,
    });
  }
};

const removeWidgetPlugin = async (
  editorSDK: FlowEditorSDK,
  hostCompRef: ComponentRef,
  slot: WidgetSlot,
) => {
  const slotDefinition = getSlotDefinition(slot, hostCompRef);

  return editorSDK.tpa.widgetPlugins.removeWidgetPlugin('', {
    ...slotDefinition,
  });
};

const getInstalledPluginsSlots = async (
  editorSDK: FlowEditorSDK,
  widgetRef: ComponentRef,
) => {
  const widgetSlots = await getWidgetSlots(editorSDK, widgetRef);
  return widgetSlots.filter(({ pluginInfo }) => pluginInfo?.widgetId);
};

const getInstalledSlotsMap = async (
  editorSDK: FlowEditorSDK,
  widgetRef: ComponentRef,
) => {
  const widgetSlots = await getInstalledPluginsSlots(editorSDK, widgetRef);

  return new Map(
    widgetSlots.map((widgetSlot) => [
      widgetSlot.pluginInfo!.widgetId,
      widgetSlot,
    ]),
  );
};

const getSlotsToRemove = async (
  editorSDK: FlowEditorSDK,
  widgetRef: ComponentRef,
  widgetsIds: WidgetId[],
) => {
  const slots = await getInstalledSlotsMap(editorSDK, widgetRef);

  return widgetsIds.reduce<WidgetSlot[]>((slotsAcc, widgetId) => {
    const slotToRemove = slots.get(widgetId);

    return slotToRemove ? [...slotsAcc, slotToRemove] : slotsAcc;
  }, []);
};

export const removeWidgetPluginsFromWidget = async (
  editorSDK: FlowEditorSDK,
  widgetRef: ComponentRef,
  widgetIds: WidgetId[],
) => {
  const slotsToRemove = await getSlotsToRemove(editorSDK, widgetRef, widgetIds);

  for (const slot of slotsToRemove) {
    await removeWidgetPlugin(editorSDK, widgetRef, slot);
  }
};
